import React, { useState, useRef, useEffect } from "react";

import SideDrawerButton from "./SideDrawerButton";

import { Link } from "react-router-dom";

import styles from "./SideDrawer.module.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb, faPlusCircle, faBolt, faMagnet, faRoute } from "@fortawesome/free-solid-svg-icons";
import { faChartLine, faStopwatch } from "@fortawesome/free-solid-svg-icons";
import { faChartSimple } from "@fortawesome/free-solid-svg-icons";
import { faWaveSquare } from "@fortawesome/free-solid-svg-icons";
import { faMobileAlt } from "@fortawesome/free-solid-svg-icons";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { faFacebookSquare } from "@fortawesome/free-brands-svg-icons";
import { faDonate, faShoppingCart, faUser } from "@fortawesome/free-solid-svg-icons";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../../contexts/AuthContext";
import { useLocale, useLocaleRouteMatch } from "../../../i18n/LocaleContext";
import { featureFlags } from "../../../utilities/featureFlags";

const SideDrawer = (props) => {
  const [subMenuShowing, setSubMenuShowing] = useState(0);
  const { isAuthenticated } = useAuth();
  const { t, localePath } = useLocale();
  const { shop, games } = featureFlags;
  const classes = [styles.SideDrawer];
  const topMaskClasses = [styles.TopMask];

  const sideDrawerRef = useRef(null);

  if (props.show) {
    classes.push(styles.Show);
    topMaskClasses.push(styles.TopMaskShow);
  }

  useEffect(() => {
    if (props.show) {
      sideDrawerRef.current.scrollTop = 0;
    } else {
      setSubMenuShowing(0);
    }
    return () => {};
  }, [props.show]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const node = sideDrawerRef.current;
    if (!node) {
      return undefined;
    }

    const root = document.documentElement;
    const updateWidth = () => {
      const width = node.getBoundingClientRect().width;
      if (width) {
        root.style.setProperty("--side-drawer-width", `${width}px`);
      }
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      updateWidth();
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
      root.style.removeProperty("--side-drawer-width");
    };
  }, []);

  const toggleSubMenuHandler = (e, subMenuIndex) => {
    setSubMenuShowing((prevValue) => {
      return subMenuIndex === prevValue ? 0 : subMenuIndex;
    });
  };

  // Check current route using shared hook (avoids useMatch hook count issues)
  const routeMatches = useLocaleRouteMatch();
  const isHome = routeMatches("/");
  const isEnergy = routeMatches("/energy", { end: false });
  const isAbout = routeMatches("/about", { end: false });
  const isVideoVault = routeMatches("/video-vault", { end: false });
  const isShop = routeMatches("/shop", { end: false });
  const isAccount = routeMatches("/account", { end: false });

  return (
    <React.Fragment>
      <div className={topMaskClasses.join(" ")} />
      <div className={classes.join(" ")} ref={sideDrawerRef}>
        <div className={styles.ContentContainer}>
          {!isHome && (
            <Link to={localePath("/")}>
              <SideDrawerButton
                subMenuIndex={0}
                clickHandler={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                {t("nav.home")}
              </SideDrawerButton>
            </Link>
          )}
          {shop && isAuthenticated && !isAccount && (
            <Link to={localePath("/account")}>
              <SideDrawerButton
                subMenuIndex={0}
                clickHandler={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                <FontAwesomeIcon className={styles.FaIcon} icon={faUser} />
                {t("nav.myAccount")}
              </SideDrawerButton>
            </Link>
          )}
          {shop && !isShop && (
            <Link to={localePath("/shop")}>
              <SideDrawerButton
                subMenuIndex={0}
                clickHandler={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                <FontAwesomeIcon className={styles.FaIcon} icon={faShoppingCart} />
                {t("nav.store")}
              </SideDrawerButton>
            </Link>
          )}
          <SideDrawerButton
            subMenuIndex={1}
            clickHandler={toggleSubMenuHandler}
            subMenuOpen={subMenuShowing}
          >
            {t("nav.games")}
          </SideDrawerButton>
          {subMenuShowing === 1 && (
            <div className={styles.SubMenu}>
              <Link
                to={localePath("/crack-the-circuit")}
                className={styles.SideDrawerButton}
                onClick={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                <SideDrawerButton
                  subMenuIndex={0}
                  subMenuBtn
                  clickHandler={() => {}}
                >
                  <FontAwesomeIcon
                    className={styles.FaIcon}
                    icon={faLightbulb}
                  />{" "}
                  {t("games.crackTheCircuit")}
                </SideDrawerButton>
              </Link>
              {games.kinematicsLab && (
                <Link to={localePath("/kinematics-lab")} className={styles.SideDrawerButton}>
                  <SideDrawerButton
                    subMenuIndex={0}
                    subMenuBtn
                    clickHandler={() => {
                      props.setShouldShowSideDrawer(false);
                    }}
                  >
                    <FontAwesomeIcon className={styles.FaIcon} icon={faChartLine} />
                    {t("games.kinematicsLab")}
                  </SideDrawerButton>
                </Link>
              )}
              {games.plotThePath && (
                <Link to={localePath("/plot-the-path")} className={styles.SideDrawerButton}>
                  <SideDrawerButton
                    subMenuIndex={0}
                    subMenuBtn
                    clickHandler={() => {
                      props.setShouldShowSideDrawer(false);
                    }}
                  >
                    <FontAwesomeIcon className={styles.FaIcon} icon={faRoute} />
                    {t("games.plotThePath")}
                  </SideDrawerButton>
                </Link>
              )}
              {!isEnergy && (
                <Link to={localePath("/energy")}>
                  <SideDrawerButton
                    subMenuIndex={0}
                    subMenuBtn
                    clickHandler={() => {
                      props.setShouldShowSideDrawer(false);
                    }}
                  >
                    <FontAwesomeIcon
                      className={styles.FaIcon}
                      icon={faChartSimple}
                    />
                    {t("games.energyBarCharts")}
                  </SideDrawerButton>
                </Link>
              )}
              <Link
                to={localePath("/polarity-shift")}
                className={styles.SideDrawerButton}
                onClick={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                <SideDrawerButton
                  subMenuIndex={0}
                  subMenuBtn
                  clickHandler={() => {}}
                >
                  <FontAwesomeIcon
                    className={styles.FaIcon}
                    icon={faPlusCircle}
                  />{" "}
                  {t("games.polarityShift")}
                </SideDrawerButton>
              </Link>
              {games.magnetismMiniGolf && (
                <Link to={localePath("/magnetism-mini-golf")} className={styles.SideDrawerButton}>
                  <SideDrawerButton
                    subMenuIndex={0}
                    subMenuBtn
                    clickHandler={() => {
                      props.setShouldShowSideDrawer(false);
                    }}
                  >
                    <FontAwesomeIcon className={styles.FaIcon} icon={faMagnet} />
                    {t("games.magnetismMiniGolf")}
                  </SideDrawerButton>
                </Link>
              )}
              <Link to={localePath("/wavemaker")}>
                <SideDrawerButton
                  subMenuIndex={0}
                  subMenuBtn
                  clickHandler={() => {
                    props.setShouldShowSideDrawer(false);
                  }}
                >
                  <FontAwesomeIcon
                    className={styles.FaIcon}
                    icon={faWaveSquare}
                  />
                  {t("games.wavemaker")}
                </SideDrawerButton>
              </Link>
              <Link
                to={localePath("/motion-mapper")}
                className={styles.SideDrawerButton}
              >
                <SideDrawerButton
                  subMenuIndex={0}
                  subMenuBtn
                  clickHandler={() => {
                    props.setShouldShowSideDrawer(false);
                  }}
                >
                  <FontAwesomeIcon
                    className={styles.FaIcon}
                    icon={faStopwatch}
                  />
                  {t("games.motionMapper")}
                </SideDrawerButton>
              </Link>
              <Link to={localePath("/visual-accelerometer")} className={styles.SideDrawerButton}>
                <SideDrawerButton
                  subMenuIndex={0}
                  subMenuBtn
                  clickHandler={() => {
                    props.setShouldShowSideDrawer(false);
                  }}
                >
                  <FontAwesomeIcon className={styles.FaIcon} icon={faMobileAlt} />
                  {t("games.visualAccelerometer")}
                </SideDrawerButton>
              </Link>
              {games.ampedUp && (
                <Link to={localePath("/amped-up")} className={styles.SideDrawerButton}>
                  <SideDrawerButton
                    subMenuIndex={0}
                    subMenuBtn
                    clickHandler={() => {
                      props.setShouldShowSideDrawer(false);
                    }}
                  >
                    <FontAwesomeIcon className={styles.FaIcon} icon={faBolt} />{" "}
                    {t("games.ampedUpArena")}
                  </SideDrawerButton>
                </Link>
              )}
            </div>
          )}

          {!isVideoVault && (
            <Link to={localePath("/video-vault")}>
              <SideDrawerButton
                subMenuIndex={0}
                clickHandler={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                {t("nav.videoVault")}
              </SideDrawerButton>
            </Link>
          )}
          {!isAbout && (
            <Link to={localePath("/about")}>
              <SideDrawerButton
                subMenuIndex={0}
                clickHandler={() => {
                  props.setShouldShowSideDrawer(false);
                }}
              >
                {t("nav.aboutUs")}
              </SideDrawerButton>
            </Link>
          )}
        </div>
        <div className={styles.Separator}>
          <FontAwesomeIcon className={styles.FaIcon} icon={faCircle} />
          <FontAwesomeIcon className={styles.FaIcon} icon={faCircle} />
          <FontAwesomeIcon className={styles.FaIcon} icon={faCircle} />
        </div>
        <div className={styles.ContentContainer}>
          <div className={styles.ContactUs}>
            <a
              href="mailto:info@universeandmore.com"
              target="_blank"
              aria-label={t("social.email")}
              className={styles.ContactLink}
            >
              <SideDrawerButton
                className={styles.ContactUs}
                clickHandler={() => {}}
                subMenuIndex={0}
              >
                <FontAwesomeIcon
                  icon={faPaperPlane}
                  className={styles.FaIcon}
                />
                {t("nav.contactUs")}
              </SideDrawerButton>
            </a>
          </div>
          <div className={styles.SocialIcons}>
            <a
              href="https://www.facebook.com/theuniverseandmore"
              target="_blank"
              aria-label={t("social.facebook")}
              className={styles.SocialLink}
            >
              <FontAwesomeIcon
                className={styles.SocialIcon}
                icon={faFacebookSquare}
              />
            </a>
          </div>
          {featureFlags.donations && (
            <div
              className={styles.DonationFooter}
              onClick={() => {
                props.openDonationModal();
              }}
            >
              <FontAwesomeIcon className={styles.FaIcon} icon={faDonate} />
              {t("nav.donate")}
            </div>
          )}
        </div>
      </div>
      <div
        className={styles.CloseButton}
        onClick={props.show ? props.closeMenu : () => {}}
      />
    </React.Fragment>
  );
};

export default SideDrawer;
